import RecordItem from '@/components/business/records-item';
import globalStore from '@/services/global.state';
import {BasicObject} from '@/types';
import {copyText, toPriceStr} from '@/utils';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {StyleSheet, View} from 'react-native';
import Text from '@components/basic/text';
import theme from '@/style';
import LazyImage from '@/components/basic/image';
import {NativeTouchableOpacity} from '@/components/basic/touchable-opacity';

const copyIcon = require('@/components/assets/icons/bets/copy.webp');
import {
  RechargeLogItem,
  WithdrawLogItem,
  TransferLogItem,
} from './records-service';
const defaultCard = require('@/assets/icons/withdraw/default-card-icon.webp');
const ICONS = {
  withdraw: require('@assets/icons/records/withdraw-new.webp'),
  recharge: require('@assets/icons/records/recharge-new.webp'),
  transfer: require('@assets/icons/records/transfer-new.webp'),
} as BasicObject;

const PAY_ADDRESS_ROW_MAX_WIDTH = 172; // 150 (text) + 14 (icon) + 8 (gap)

const styles = StyleSheet.create({
  payAddressText: {
    flex: 1,
    minWidth: 0,
    maxWidth: 150,
  },
  payAddressRow: {
    maxWidth: PAY_ADDRESS_ROW_MAX_WIDTH,
  },
});

const Item = (
  props:
    | {
        type: 'withdraw';
        info: WithdrawLogItem;
      }
    | {
        type: 'recharge';
        info: RechargeLogItem;
      }
    | {
        type: 'transfer';
        info: TransferLogItem;
      },
) => {
  const {i18n} = useTranslation();
  const {type, info} = props;

  const isCrypto =
    (info as WithdrawLogItem | RechargeLogItem).payCategory === 2;

  const subtitle = React.useMemo(() => {
    if (!isCrypto) {
      return undefined;
    }
    if (type === 'recharge') {
      return i18n.t('records.label.depositFromCryptoWallet');
    }
    if (type === 'withdraw') {
      return i18n.t('records.label.withdrawFromCryptoWallet');
    }
    return undefined;
  }, [isCrypto, type, i18n]);

  const detailInfo = React.useMemo(() => {
    switch (type) {
      case 'withdraw': {
        const withdrawInfo = info as WithdrawLogItem;
        if (withdrawInfo.payCategory === 2) {
          const payAddressValue = withdrawInfo.payAddress ?? '-';
          return [
            {
              key: i18n.t('records.label.received'),
              value: `${toPriceStr(withdrawInfo.systemSupportAmount ?? 0, {
                thousands: true,
                spacing: true,
                currency: globalStore.currency,
              })}`,
            },
            {
              key: i18n.t('records.label.payAddress'),
              value: (
                <View
                  style={[
                    theme.flex.row,
                    theme.flex.centerByCol,
                    styles.payAddressRow,
                  ]}>
                  <Text
                    numberOfLines={1}
                    style={styles.payAddressText}
                    color={theme.fontColor.second}>
                    {payAddressValue}
                  </Text>
                  {payAddressValue !== '-' ? (
                    <NativeTouchableOpacity
                      onPress={() => {
                        copyText(withdrawInfo.payAddress ?? '');
                        globalStore.globalSucessTotal(i18n.t('copy-success'));
                      }}
                      style={[theme.margin.lefts]}>
                      <LazyImage
                        imageUrl={copyIcon}
                        width={14}
                        height={14}
                        occupancy="transparent"
                      />
                    </NativeTouchableOpacity>
                  ) : null}
                </View>
              ),
            },
          ];
        }
        return [
          {
            key: i18n.t('records.label.received'),
            value: `${toPriceStr(withdrawInfo.reward, {
              thousands: true,
              spacing: true,
              currency: globalStore.currency,
            })}`,
          },
          {
            key: i18n.t('records.label.commission'),
            value: `- ${toPriceStr(
              withdrawInfo.withdrawFeeRate * withdrawInfo.paidAmount,
              {
                thousands: true,
                spacing: true,
                currency: globalStore.currency,
              },
            )}`,
          },
          {
            key: i18n.t('records.label.withdrawChannel'),
            value: (
              <View style={[theme.flex.row, theme.flex.centerByCol]}>
                <View style={[theme.flex.alignEnd, theme.margin.rights]}>
                  <Text color={theme.fontColor.second}>
                    {withdrawInfo.bankName}
                  </Text>
                  <Text color={'#5F6975'}>{withdrawInfo.userCardNumber}</Text>
                </View>
                <LazyImage
                  imageUrl={defaultCard}
                  width={32}
                  height={32}
                  occupancy="transparent"
                />
              </View>
            ),
          },
        ];
      }
      case 'recharge':
        return [
          {
            key: `${i18n.t('label.recharge')} ${i18n.t(
              'records.label.amount',
            )}`,
            value: toPriceStr(info.paidAmount, {
              thousands: true,
              spacing: true,
              currency: globalStore.currency,
            }),
          },
          {
            key: `${i18n.t('label.recharge')} ${i18n.t(
              'records.label.reward',
            )}`,
            value:
              '+' +
              toPriceStr(info.reward, {
                thousands: true,
                spacing: true,
                currency: globalStore.currency,
              }),
          },

          {
            key: `${i18n.t('label.recharge')} ${i18n.t(
              'records.label.channel',
            )}`,
            value: info.channelName,
          },
        ];
      case 'transfer':
        return [
          {
            key: `${i18n.t('label.transfer')} ${i18n.t(
              'records.label.amount',
            )}`,
            value: toPriceStr(info.price, {
              thousands: true,
              spacing: true,
              currency: globalStore.currency,
            }),
          },
          {
            key: `${i18n.t('label.transfer')} ${i18n.t(
              'records.label.reward',
            )}`,
            value:
              '+' +
              toPriceStr(info.reward || 0, {
                thousands: true,
                spacing: true,
                currency: globalStore.currency,
              }),
          },
          {
            key: i18n.t('records.label.orderNum'),
            value: info.order,
          },
        ];
    }
  }, [info, type, i18n]);

  return (
    <RecordItem
      showBalance={false}
      type={999}
      hasStatus={type !== 'transfer'}
      status={info.status}
      iconUrl={ICONS[type]}
      hasAmountPrefix={false}
      hasMore
      typeName={type}
      subtitle={subtitle}
      statusTip={
        info.status === 'PROCESS'
          ? i18n.t('records.tip.renewTime')
          : info.status === 'FAILED'
          ? i18n.t('records.tip.error')
          : ''
      }
      moreData={detailInfo}
      amount={info.paidAmount || info.price}
      balance={info.balance}
      time={info.appDate || info.createTime}
      orderNum={(info as RechargeLogItem | WithdrawLogItem)?.orderNo}
    />
  );
};

export default Item;
